import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { webApi } from '../services/api';
import { LoadingState, StatusBanner } from '../components/ui';
import { WithdrawIcon, PoolSplitIcon, ChevronRightSmall } from '../components/TransactionActionIcons';
import { TransactionDetailSheet } from '../components/TransactionDetailSheet';
import { TransactionListRow } from '../components/TransactionListItems';
import { formatInr } from '../utils/transactionDisplay';
import { isCanceledRequest } from '../utils/abortRequest';

function TransactionsPage() {
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailTx, setDetailTx] = useState(null);

  useEffect(() => {
    const ac = new AbortController();
    const { signal } = ac;
    (async () => {
      try {
        const data = await webApi.getTransactionsOverview(5, { signal });
        if (signal.aborted) return;
        setOverview(data || null);
        setError('');
      } catch (err) {
        if (signal.aborted || isCanceledRequest(err)) return;
        setError(err.message || 'Failed to load transactions');
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, []);

  if (loading) return <LoadingState message="Loading transactions..." />;

  const monthly = overview?.monthlyEarnings ?? 0;
  const available = overview?.availableToWithdraw ?? 0;
  const tipMode = overview?.defaultTipMode === 'pool' ? 'pool' : 'individual';
  const recent = overview?.recentTransactions || [];

  return (
    <div className="page tx-page">
      <h1 className="tx-page-title">Transactions</h1>
      <StatusBanner kind="error" message={error} />

      <div className="tx-metrics-row">
        <div className="tx-metric-card tx-metric-card--purple">
          <p className="tx-metric-label">Monthly Earnings</p>
          <p className="tx-metric-value">{formatInr(monthly)}</p>
        </div>
        <div className="tx-metric-card tx-metric-card--green">
          <p className="tx-metric-label">Available to withdraw</p>
          <p className="tx-metric-value">{formatInr(available)}</p>
        </div>
      </div>

      <div className="tx-action-stack">
        <Link to="/payout" className="tx-action-card">
          <span className="tx-action-icon-circle" aria-hidden="true">
            <WithdrawIcon size={24} />
          </span>
          <div className="tx-action-text">
            <span className="tx-action-title">Request Payout</span>
            <span className="tx-action-sub">Withdraw your earnings</span>
          </div>
          <ChevronRightSmall />
        </Link>
        {tipMode === 'pool' ? (
          <Link to="/distribution" className="tx-action-card">
            <span className="tx-action-icon-circle" aria-hidden="true">
              <PoolSplitIcon size={22} />
            </span>
            <div className="tx-action-text">
              <span className="tx-action-title">Pool Distribution</span>
              <span className="tx-action-sub">Distribute pool balance among employees</span>
            </div>
            <ChevronRightSmall />
          </Link>
        ) : null}
      </div>

      <div className="tx-recent-head">
        <h2 className="tx-recent-title">Recent Transactions</h2>
        <Link to="/transactions/all" className="tx-view-all">
          View more
        </Link>
      </div>

      <div className="tx-recent-list">
        {recent.length === 0 ? (
          <div className="tx-empty-inline">No transactions yet.</div>
        ) : (
          recent.map((tx) => (
            <TransactionListRow key={tx.id} transaction={tx} onViewDetails={setDetailTx} />
          ))
        )}
      </div>

      {detailTx ? <TransactionDetailSheet transaction={detailTx} onClose={() => setDetailTx(null)} /> : null}
    </div>
  );
}

export default TransactionsPage;
